import { useEffect, useRef } from "react";
import { read } from "./net/rally.ts";
import { goTo } from "./router.ts";

export type Way = -1 | 1;

const AN_ID = /^conversation-([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

export function isTheStep(event: {
  key: string;
  metaKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
  shiftKey: boolean;
}): Way | null {
  if (event.altKey || event.shiftKey) return null;
  if (event.metaKey === event.ctrlKey) return null;
  if (event.key === "ArrowUp") return -1;
  if (event.key === "ArrowDown") return 1;
  return null;
}

export function stepTo(order: readonly string[], at: string | null, way: Way): string | null {
  if (order.length === 0) return null;
  const here = at ? order.indexOf(at) : -1;

  if (here < 0) return (way === 1 ? order[0] : order[order.length - 1]) ?? null;
  const next = here + way;
  if (next < 0 || next >= order.length) return null;
  return order[next] ?? null;
}

export function isTyping(on: Element | null): boolean {
  const el = on as (Element & { isContentEditable?: boolean; tagName: string }) | null;
  if (!el) return false;
  if (el.tagName === "INPUT" || el.tagName === "TEXTAREA") return true;
  if (el.isContentEditable) return true;
  return false;
}

export function orderOnTheScreen(root: Document | HTMLElement): string[] {
  const seen: string[] = [];
  for (const node of root.querySelectorAll('[data-testid^="conversation-"]')) {
    const id = AN_ID.exec(node.getAttribute("data-testid") ?? "")?.[1];

    if (id && !seen.includes(id)) seen.push(id);
  }
  return seen;
}

type WhereIAm = {
  personal: Array<{ id: string }>;
  projects: Array<{ conversations: Array<{ id: string }> }>;
};

export function orderFromTheServer(where: WhereIAm): string[] {
  return [
    ...where.personal.map((one) => one.id),
    ...where.projects.flatMap((project) => project.conversations.map((one) => one.id)),
  ];
}

function bringIntoView(id: string): void {
  const row = document.querySelector(`[data-testid="conversation-${id}"]`);
  (row as HTMLElement | null)?.scrollIntoView?.({ block: "nearest" });
}

export function useStepping(at: string | null, on: boolean): void {
  const remembered = useRef<string[]>([]);
  const where = useRef<string | null>(at);
  where.current = at;

  useEffect(() => {
    if (!on || typeof document === "undefined") return;
    if (orderOnTheScreen(document).length > 0) return;
    const asking = at;
    let gone = false;
    read<WhereIAm>("where.i.am")
      .then((answer) => {
        if (gone || where.current !== asking) return;
        if (orderOnTheScreen(document).length === 0) remembered.current = orderFromTheServer(answer);
      })
      .catch(() => undefined);
    return () => {
      gone = true;
    };
  }, [on, at]);

  useEffect(() => {
    if (!on || typeof window === "undefined") return;
    const onKey = (event: KeyboardEvent) => {
      const way = isTheStep(event);
      if (way === null) return;
      if (isTyping(document.activeElement)) return;
      if (isTyping(event.target as Element | null)) return;
      const drawn = orderOnTheScreen(document);
      if (drawn.length > 0) remembered.current = drawn;
      const order = drawn.length > 0 ? drawn : remembered.current;
      const next = stepTo(order, where.current, way);

      if (order.length > 0) event.preventDefault();
      if (!next || next === where.current) return;
      goTo(`/c/${next}`);

      setTimeout(() => bringIntoView(next), 0);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [on]);
}
